import { useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useUIStore } from "@/stores/ui-store";
import { usePageTitle } from "@/hooks/use-page-title";
import { Header } from "./header";
import { Sidebar } from "./sidebar";
import BannerAds from "../BannerAds";
import { book } from "@/routers/books";

interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const location = useLocation();
  const {
    mobileDrawerOpen,
    setMobileDrawerOpen,
  } = useUIStore();

  // Update page title for screen reader announcements
  usePageTitle();

  // Get clean pathname
  let clearPath = location.pathname;
  if (clearPath.endsWith('/') && clearPath !== '/') {
    clearPath = clearPath.slice(0, -1);
  }

  // Keyboard shortcuts
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Close mobile drawer on Escape
      if (event.key === "Escape" && mobileDrawerOpen) {
        setMobileDrawerOpen(false);
      }
    },
    [mobileDrawerOpen, setMobileDrawerOpen]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Close mobile drawer on route change
  useEffect(() => {
    if (mobileDrawerOpen) {
      setMobileDrawerOpen(false);
    }
  }, [location.pathname]); // Close when route changes

  // Close mobile drawer on resize to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && mobileDrawerOpen) {
        setMobileDrawerOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [mobileDrawerOpen, setMobileDrawerOpen]);

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background">
      {/* Skip to main content link */}
      <a
        href="#main-content"
        className={cn(
          "sr-only focus:not-sr-only",
          "focus:absolute focus:top-4 focus:left-4 focus:z-[100]",
          "focus:px-4 focus:py-2",
          "focus:bg-primary focus:text-primary-foreground",
          "focus:rounded-md focus:shadow-lg",
          "focus:outline-none focus:ring-2 focus:ring-ring"
        )}
      >
        Skip to main content
      </a>

      {/* Desktop Sidebar */}
      <div className="hidden md:flex">
        <Sidebar />
      </div>

      {/* Mobile Drawer Overlay */}
      {mobileDrawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm md:hidden"
          onClick={() => setMobileDrawerOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile Drawer */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 md:hidden",
          "transition-transform duration-300 ease-in-out",
          mobileDrawerOpen ? "translate-x-0" : "-translate-x-full"
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <Sidebar />
        <button
          onClick={() => setMobileDrawerOpen(false)}
          className={cn(
            "absolute right-2 top-2 rounded-md p-2",
            "text-sidebar-foreground/60 hover:text-sidebar-foreground",
            "hover:bg-sidebar-accent/50",
            "transition-colors duration-150",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-sidebar-ring"
          )}
          aria-label="Close sidebar"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Main Content Area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile Header with Hamburger */}
        <div className="flex items-center gap-4 border-b border-border bg-background px-4 h-14 md:hidden">
          <button
            onClick={() => setMobileDrawerOpen(true)}
            className={cn(
              "inline-flex items-center justify-center rounded-md",
              "h-9 w-9 text-foreground/80",
              "hover:bg-accent hover:text-foreground",
              "transition-colors duration-150",
              "focus-visible:outline-none focus-visible:ring-2",
              "focus-visible:ring-ring focus-visible:ring-offset-2"
            )}
            aria-label="Open sidebar"
          >
            <Menu className="h-5 w-5" />
          </button>
          <span className="font-semibold text-lg">BevNET Account</span>
        </div>

        {/* Desktop Header */}
        <div className="hidden md:block">
          <Header />
        </div>

        {/* Main Content */}
        <main id="main-content" className="flex-1 overflow-y-auto scrollbar-thin">
          <div className="container mx-auto p-4 lg:p-6">
            {/* Banner Ads - hide on invoice pages */}
            {!clearPath.includes(book.invoices.invoice) && <BannerAds />}
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
